import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
} from 'chart.js';
import { Line, Bar, Radar } from 'react-chartjs-2';
import { Card, Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaChartLine, FaChartBar, FaChartArea } from 'react-icons/fa';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale
);

const AnalyticsCharts = ({ userBusinesses }) => {
  const [activeChart, setActiveChart] = useState('views');

  // Generate sample analytics data
  const generateAnalyticsData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
    
    return {
      views: months.map(() => Math.floor(Math.random() * 2000) + 500),
      reviews: months.map(() => Math.floor(Math.random() * 50) + 10),
      contacts: months.map(() => Math.floor(Math.random() * 100) + 20),
      favorites: months.map(() => Math.floor(Math.random() * 30) + 5),
      months
    };
  };

  const generateBusinessComparison = () => {
    return userBusinesses?.slice(0, 5).map(business => ({
      name: (business?.name || business?.businessName || 'Business').length > 12 ? (business?.name || business?.businessName || 'Business').substring(0, 12) + '...' : (business?.name || business?.businessName || 'Business'),
      views: business?.views || business?.view_count || Math.floor(Math.random() * 1000) + 100,
      reviews: business?.reviews_count || Math.floor(Math.random() * 25) + 1,
      rating: business?.rating || (Math.random() * 2 + 3).toFixed(1),
      contacts: Math.floor(Math.random() * 50) + 5
    })) || [];
  };

  const generatePerformanceRadar = () => {
    const business = userBusinesses?.[0] || {};
    return {
      views: business?.views || business?.view_count || Math.floor(Math.random() * 100),
      reviews: business?.reviews_count || Math.floor(Math.random() * 100),
      rating: (business?.rating || Math.random() * 2 + 3) * 20,
      contacts: Math.floor(Math.random() * 100),
      favorites: Math.floor(Math.random() * 100),
      shares: Math.floor(Math.random() * 100)
    };
  };

  const analyticsData = generateAnalyticsData();
  const businessComparison = generateBusinessComparison();
  const radarData = generatePerformanceRadar();

  // Chart configurations
  const analyticsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#4a5568',
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      title: {
        display: true,
        text: 'Analytics Overview',
        color: '#2d3748',
        font: {
          size: 16,
          weight: '600'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 4,
        hoverRadius: 6
      }
    }
  };

  const analyticsChartData = {
    labels: analyticsData.months,
    datasets: [
      {
        label: 'Views',
        data: analyticsData.views,
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Reviews',
        data: analyticsData.reviews,
        borderColor: '#ed8936',
        backgroundColor: 'rgba(237, 137, 54, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Contacts',
        data: analyticsData.contacts,
        borderColor: '#48bb78',
        backgroundColor: 'rgba(72, 187, 120, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }
    ]
  };

  const comparisonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#4a5568',
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      title: {
        display: true,
        text: 'Business Performance Comparison',
        color: '#2d3748',
        font: {
          size: 16,
          weight: '600'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          },
          maxRotation: 45
        }
      }
    }
  };

  const comparisonChartData = {
    labels: businessComparison.map(b => b.name),
    datasets: [
      {
        label: 'Views',
        data: businessComparison.map(b => b.views),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: '#667eea',
        borderWidth: 1
      },
      {
        label: 'Reviews',
        data: businessComparison.map(b => b.reviews),
        backgroundColor: 'rgba(237, 137, 54, 0.8)',
        borderColor: '#ed8936',
        borderWidth: 1
      },
      {
        label: 'Contacts',
        data: businessComparison.map(b => b.contacts),
        backgroundColor: 'rgba(72, 187, 120, 0.8)',
        borderColor: '#48bb78',
        borderWidth: 1
      }
    ]
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Performance Radar',
        color: '#2d3748',
        font: {
          size: 16,
          weight: '600'
        }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: '#e2e8f0'
        },
        pointLabels: {
          color: '#4a5568',
          font: {
            size: 12,
            weight: '500'
          }
        },
        ticks: {
          color: '#718096',
          font: {
            size: 10
          }
        }
      }
    }
  };

  const radarChartData = {
    labels: ['Views', 'Reviews', 'Rating', 'Contacts', 'Favorites', 'Shares'],
    datasets: [
      {
        label: 'Performance',
        data: [
          radarData.views,
          radarData.reviews,
          radarData.rating,
          radarData.contacts,
          radarData.favorites,
          radarData.shares
        ],
        backgroundColor: 'rgba(102, 126, 234, 0.2)',
        borderColor: '#667eea',
        borderWidth: 2,
        pointBackgroundColor: '#667eea',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      }
    ]
  };

  return (
    <Row className="mb-4">
      {/* Chart Type Selector */}
      <Col md={12} className="mb-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="chart-selector-card">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-muted">Analytics Dashboard</h6>
                <ButtonGroup size="sm">
                  <Button
                    variant={activeChart === 'views' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('views')}
                  >
                    <FaChartLine className="me-1" />
                    Views
                  </Button>
                  <Button
                    variant={activeChart === 'comparison' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('comparison')}
                  >
                    <FaChartBar className="me-1" />
                    Comparison
                  </Button>
                  <Button
                    variant={activeChart === 'radar' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('radar')}
                  >
                    <FaChartArea className="me-1" />
                    Performance
                  </Button>
                </ButtonGroup>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Col>

      {/* Dynamic Chart Display */}
      <Col md={12}>
        <motion.div
          key={activeChart}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="chart-card">
            <Card.Body>
              <div style={{ height: '400px' }}>
                {activeChart === 'views' && (
                  <Line data={analyticsChartData} options={analyticsOptions} />
                )}
                {activeChart === 'comparison' && (
                  <Bar data={comparisonChartData} options={comparisonOptions} />
                )}
                {activeChart === 'radar' && (
                  <Radar data={radarChartData} options={radarOptions} />
                )}
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Col>
    </Row>
  );
};

export default AnalyticsCharts;
